/**
 * CTA section for level pages linking to pricing and trial booking.
 *
 * What: Provides clear calls-to-action for viewing pricing and booking a trial class.
 * Why: Guides conversion at the end of level pages with pricing link and trial CTA.
 * When: Update when changing level page CTA strategy or layout.
 */
import { Box, Container, Paper, Stack, Typography } from '@mui/material';

import LandingBackgroundAccent from '@/components/landing/LandingBackgroundAccent';
import LandingSectionShell from '@/components/landing/LandingSectionShell';
import {
  colors,
  containerPadding,
  radii,
  shadows,
} from '@/components/landing/landingTokens';
import LandingTrialCtas from '@/components/landing/LandingTrialCtas';
import { useTuitionSubject } from '@/contexts/tuitionSubjectContext';

interface LevelCtaSectionProps {
  levelId: string;
  sourceLocation?: string;
}

function LevelCtaSection({ levelId, sourceLocation }: LevelCtaSectionProps) {
  const tuition = useTuitionSubject();
  const titleId = 'level-cta-title';

  return (
    <LandingSectionShell
      id="cta"
      ariaLabelledby={titleId}
      mb={{ xs: 4, md: 6 }}
    >
      <Container maxWidth="lg" disableGutters sx={{ px: containerPadding.px }}>
        <Paper
          elevation={0}
          sx={{
            position: 'relative',
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            boxSizing: 'border-box',
            p: { xs: 2.5, sm: 3.5, md: 4 },
            borderRadius: { xs: radii.md, md: radii.lg },
            backgroundColor: colors.backgroundAlt,
            color: colors.ink,
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.hero,
            overflow: 'hidden',
          }}
        >
          <LandingBackgroundAccent />

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 3, md: 4 }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            sx={{ position: 'relative', zIndex: 1 }}
          >
            <Stack
              spacing={{ xs: 1.25, sm: 1.5 }}
              sx={{
                maxWidth: { xs: '100%', sm: 720 },
                flex: 1,
                textAlign: { xs: 'center', md: 'left' },
                alignItems: { xs: 'center', md: 'flex-start' },
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  width: 'fit-content',
                  px: 2,
                  py: 1,
                  borderRadius: radii.pill,
                  backgroundColor: colors.washAmber,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.accentOrangeDark,
                    fontWeight: 800,
                    fontSize: 12,
                    letterSpacing: 1.5,
                    overflowWrap: 'break-word',
                  }}
                >
                  Ready to start?
                </Typography>
              </Box>

              <Typography
                id={titleId}
                component="h2"
                sx={{
                  fontWeight: 800,
                  color: colors.ink,
                  fontSize: { xs: 22, sm: 26, md: 28 },
                  letterSpacing: '-0.02em',
                  lineHeight: { xs: 1.25, sm: 1.2 },
                }}
              >
                {`Try a ${tuition.trial} and see how DeepThink works`}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: colors.inkMuted,
                  lineHeight: { xs: 1.7, sm: 1.65 },
                  fontSize: { xs: 15, sm: 16 },
                }}
              >
                Experience our teaching approach firsthand. No commitment
                required.
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: colors.inkSubtle,
                  fontWeight: 700,
                  fontSize: { xs: 13, sm: 14 },
                  mt: 0.5,
                }}
              >
                {tuition.subject === 'mathematics'
                  ? 'Free trial • No credit card required'
                  : 'Class registration for the 2027 school year is open.'}
              </Typography>
            </Stack>

            <LandingTrialCtas
              sourceLocation={sourceLocation || `level_${levelId}_cta`}
              sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}
            />
          </Stack>
        </Paper>
      </Container>
    </LandingSectionShell>
  );
}

export default LevelCtaSection;
